// @ts-nocheck
// Note: Recharts has type incompatibilities with React 18/19. Suppressing type checks for this file.
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useOutletContext } from 'react-router-dom';
import type { ResolvedLoanBook } from '@valutten/shared-schemas';
import { resolveLoanBookSelection, getDisplayLoanBooks } from '../../utils/loanBookResolution';
import { useUserData } from '../../hooks/useUserData';
import { useCompanyData } from '../../hooks/useCompanyData';
import { useClawbacks } from '../../hooks/useClawbacks';
import {
  useTrailRiskStatusForRefs,
  refKey,
  type TrailRiskRef,
} from '../../hooks/useTrailRiskStatusForRefs';
import { TrailRiskFollowUp } from '../../components/contacts/TrailRiskFollowUp';
import { useCompanyDataYearRange } from '../../hooks/useCompanyDataYearRange';
import { normalizeBrokerName } from '../../utils/dataProcessing';
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip } from 'recharts';
import { formatCurrency } from '../../utils/formatters';
import { COMMISSION_GST_BASIS } from '../../utils/commissionDisplay';
import { RotateCcw } from 'lucide-react';
import PageHeader from '../../components/common/PageHeader';
import { SkeletonReportPage } from '../../components/common/SkeletonLoader';
import ClawbackReasonStatCards from '../../components/clawbacks/ClawbackReasonStatCards';
import { useAuth } from '../../hooks/useAuth';
import { BROKER_FILTERED_ADMIN_COPY } from '../../utils/brokerFilteredAdmin';
import {
  ClawbackReason,
  CLAWBACK_REASON_LABELS,
  CLAWBACK_REASON_COLORS,
  ALL_CLAWBACK_REASONS,
} from '../../types/clawbackReasons';

interface Office {
    id: string;
    name: string;
}

interface FileTemplate {
    id: string;
    name: string;
    isAcquiredLoanBook: boolean;
}

// ============================================================================
// JEFF DEAN PRINCIPLE: Utility functions at module scope for reusability
// ============================================================================

/**
 * Formats a clawback rate percentage for display
 * Shows 1 decimal place for rates between 0-1% (e.g., 0.5% instead of 0%)
 * Rounds to whole number for rates >= 1%
 * @param rate - The rate as a percentage (0-100)
 * @returns Formatted string (e.g., "0.5" or "8")
 */
const formatClawbackRate = (rate: number): string => {
  if (rate === 0) return '0';
  if (rate > 0 && rate < 1) {
    // Show 1 decimal place for sub-1% rates to avoid misleading 0%
    return rate.toFixed(1);
  }
  return Math.round(rate).toString();
};

/**
 * Extracts clawback percentage from comments field
 * Handles various formats: "50% clawback", "100% claw back", "clawback 75%"
 * @param comments - Raw comments string
 * @returns Percentage (0-100) or 100 as default
 */
const extractClawbackPercentage = (comments: string | null | undefined): number => {
  if (!comments) return 100; // Default to 100% if no info

  const percentMatch = comments.match(/(\d+)%\s*(?:clawback|claw\s*back)/i);
  if (percentMatch) {
    const percentage = parseInt(percentMatch[1], 10);
    // Validate range
    if (percentage >= 0 && percentage <= 100) return percentage;
  }

  // Default to 100% if pattern not found or invalid
  return 100;
};

/**
 * Safely formats Firestore timestamp or Date to display object for stacked rendering
 * @param dateValue - Firestore Timestamp or Date object
 * @returns Object with dayMonth and year for stacked display, or null if invalid
 */
const formatDateStacked = (dateValue: any): { dayMonth: string; year: string } | null => {
  if (!dateValue) return null;

  try {
    const date = dateValue?.toDate ? dateValue.toDate() : new Date(dateValue);
    if (isNaN(date.getTime())) return null;

    // Always use 3-letter month abbreviation
    const day = date.getDate();
    const monthShort = date.toLocaleDateString('en-AU', { month: 'short' }).slice(0, 3);
    const year = date.getFullYear().toString();

    return {
      dayMonth: `${day} ${monthShort}`,
      year,
    };
  } catch (error) {
    console.error('[ClawbackPage] Date formatting error:', error);
    return null;
  }
};

/**
 * Stacked date component for consistent table display
 */
const StackedDate: React.FC<{ dateValue: any }> = ({ dateValue }) => {
  const formatted = formatDateStacked(dateValue);
  if (!formatted) return <span className="text-gray-500">N/A</span>;

  return (
    <div className="text-center leading-tight">
      <div className="text-gray-300">{formatted.dayMonth}</div>
      <div className="text-gray-500 text-xs">{formatted.year}</div>
    </div>
  );
};

const ClawbackPage = () => {
    // Setting a clawback reason is default-denied to a broker-restricted admin
    // at the callable chokepoint; disable the selector rather than let it 403.
    const { isBrokerFilteredAdmin } = useAuth();
    const { userData } = useUserData();
    const { companyData } = useCompanyData();
    const { userVisibleOffices, userVisibleTemplates, loanBooks = [] } = useOutletContext<{ userVisibleOffices: Office[]; userVisibleTemplates: any[]; loanBooks: ResolvedLoanBook[] }>();

    // ✅ DYNAMIC YEAR RANGE: Fetch from BigQuery for year dropdown population
    const { earliestYear, latestYear } = useCompanyDataYearRange({
        companyId: userData?.companyId,
    });

    const [selectedBroker, setSelectedBroker] = useState(''); // Display name for dropdown
    const [selectedBrokerId, setSelectedBrokerId] = useState<string | undefined>(undefined); // ID for backend
    const [selectedOffice, setSelectedOffice] = useState<string>('');
    const [selectedTemplate, setSelectedTemplate] = useState<string>('all');

    const resolvedParams = useMemo(() =>
        resolveLoanBookSelection(selectedTemplate, loanBooks),
        [selectedTemplate, loanBooks]
    );

    const [sortBy, setSortBy] = useState('amount'); // column key
    const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
    const [chartYear, setChartYear] = useState(new Date().getFullYear());
    const [availableYears, setAvailableYears] = useState<number[]>([]);
    const [brokers, setBrokers] = useState<string[]>([]);
    const [fileTemplates, setFileTemplates] = useState<FileTemplate[]>([]);
    const [displayClawbacks, setDisplayClawbacks] = useState<any[]>([]);
    const [selectedReasonFilter, setSelectedReasonFilter] = useState<ClawbackReason | 'all' | 'needs_categorization'>('all');
    // Store ALL brokers for dropdown (not filtered by current selection)
    const [allBrokersForDropdown, setAllBrokersForDropdown] = useState<Array<{brokerId: string, brokerDisplay: string}>>([]);

    // Fetch clawback data from BigQuery via backend function
    const {
        clawbacks: allClawbacks,
        loading,
        error: hookError,
        totalClawbackCount,
        totalClawbackAmountCents,
        totalUpfrontCommissionCents,
        totalTrailCommissionCents,
        brokerBreakdown,
        templateBreakdown,
        metricsByReason,
        setClawbackReason,
        isSaving,
        selectedIds,
        toggleSelection,
        selectAll,
        clearSelection,
    } = useClawbacks({
        companyId: userData?.companyId || '',
        year: chartYear,
        yearType: 'calendar',
        // Use selectedBrokerId for admin filtering, or user's own brokerId if they're a broker
        brokerId: userData?.role === 'broker' ? userData?.brokerId : selectedBrokerId,
        officeId: selectedOffice || undefined,
        templateId: resolvedParams.templateId,
        entityCode: resolvedParams.entityCode,
    });

    const error = hookError || '';

    // ========================================================================
    // ✅ FIX (2026-01-05): Dynamic year range from BigQuery
    // Always include current year even if no data exists yet
    // ========================================================================
    useEffect(() => {
        const currentYear = new Date().getFullYear();
        const dynamicLatestYear = Math.max(latestYear ?? currentYear, currentYear);
        const dynamicEarliestYear = earliestYear ?? (currentYear - 4);
        const dynamicYears: number[] = [];
        for (let year = dynamicLatestYear; year >= dynamicEarliestYear; year--) {
            dynamicYears.push(year);
        }
        setAvailableYears(dynamicYears);
    }, [earliestYear, latestYear]); // Re-run when year range changes

    // ========================================================================
    // Templates from centralized context (no per-page Firestore query)
    // ========================================================================
    useEffect(() => {
        const templates = (userVisibleTemplates || []).map((t: any) => ({
            id: t.id,
            name: t.templateName || t.name || t.id,
            isAcquiredLoanBook: t.isAcquiredLoanBook || false,
        }));
        templates.sort((a, b) => a.name.localeCompare(b.name));
        setFileTemplates(templates);
    }, [userVisibleTemplates]);

    // ========================================================================
    // Extract brokers for filter dropdown (prefer brokerBreakdown for display names)
    // Deduplicate using Set to handle data quality issues (same name, different IDs)
    // ========================================================================
    useEffect(() => {
        // Use brokerBreakdown if available (has proper display names)
        if (brokerBreakdown && brokerBreakdown.length > 0) {
            const uniqueBrokers = Array.from(new Set(
                brokerBreakdown
                    .map((b: any) => b.brokerDisplay)
                    .filter(Boolean)
            )).sort();
            setBrokers(uniqueBrokers);
            return;
        }

        // Fallback: extract from records
        if (!allClawbacks || allClawbacks.length === 0) {
            setBrokers([]);
            return;
        }

        // Extract unique brokers with Set for O(n) performance
        const uniqueBrokers = Array.from(
            new Set(
                allClawbacks
                    .map((d: any) => d.broker)
                    .filter(Boolean)
            )
        ).sort();
        setBrokers(uniqueBrokers);
    }, [allClawbacks, brokerBreakdown]);

    // ========================================================================
    // FIX: Populate ALL brokers for dropdown when we have unfiltered data
    // Only update when: no broker filter AND have data AND list is empty
    // This keeps the full list even after a broker is selected
    // ========================================================================
    useEffect(() => {
        // Populate when we have unfiltered broker data and haven't populated yet
        if (!selectedBrokerId && brokerBreakdown && brokerBreakdown.length > 0 && allBrokersForDropdown.length === 0) {
            console.log('[ClawbackPage] Populating allBrokersForDropdown with', brokerBreakdown.length, 'brokers');
            setAllBrokersForDropdown(brokerBreakdown.map((b: any) => ({
                brokerId: b.brokerId,
                brokerDisplay: b.brokerDisplay,
            })));
        }
    }, [selectedBrokerId, brokerBreakdown, allBrokersForDropdown.length]);

    // Reset broker dropdown list when year changes (we'll get fresh data for new year)
    useEffect(() => {
        setAllBrokersForDropdown([]);
        setSelectedBrokerId(undefined);
        setSelectedBroker('');
    }, [chartYear]);

    // ========================================================================
    // JEFF DEAN PRINCIPLE: Memoize expensive computations to avoid re-renders
    // Chart data respects broker filter for visual consistency
    // ========================================================================
    const monthlyChartData = useMemo(() => {
        // Apply broker filter if selected (now also filtered by backend, but keep for chart consistency)
        const chartClawbacks = selectedBrokerId
            ? allClawbacks.filter((record: any) => record.brokerId === selectedBrokerId)
            : allClawbacks;

        // Pre-allocate array for all 12 months
        const monthlyData = Array.from({ length: 12 }, (_, monthIndex) => {
            // Filter clawbacks for this specific month using invoicedDate
            // (when the clawback was DEDUCTED - matches the year filter)
            const monthClawbacks = chartClawbacks.filter((record: any) => {
                try {
                    const date = record.invoicedDate?.toDate ?
                        record.invoicedDate.toDate() :
                        new Date(record.invoicedDate);

                    if (!date || isNaN(date.getTime())) return false;

                    return date.getFullYear() === chartYear && date.getMonth() === monthIndex;
                } catch (error) {
                    console.warn('[ClawbackPage] Invalid date in chart data:', record.id);
                    return false;
                }
            });

            // Calculate total clawback amount for the month (in dollars for chart display)
            const totalAmountCents = monthClawbacks.reduce((sum, record) => {
                try {
                    // amountExGst is in cents
                    const amount = typeof record.amountExGst === 'number' ? record.amountExGst : parseFloat(record.amountExGst || '0');

                    // Validate amount
                    if (isNaN(amount)) return sum;

                    // Sum the clawback amounts
                    return sum + amount;
                } catch (error) {
                    console.warn('[ClawbackPage] Error calculating clawback amount:', record.id);
                    return sum;
                }
            }, 0);
            // Convert to dollars for chart display
            const totalAmount = totalAmountCents / 100;

            return {
                month: new Date(chartYear, monthIndex).toLocaleString('default', { month: 'short' }),
                amount: totalAmount,
                count: monthClawbacks.length,
                fullMonthName: new Date(chartYear, monthIndex).toLocaleString('default', { month: 'long' })
            };
        });

        return monthlyData;
    }, [allClawbacks, chartYear, selectedBrokerId]);

    const CustomTooltip = ({ active, payload, label }: any) => {
        if (active && payload && payload.length) {
            const data = payload[0].payload;
            return (
                <div className="bg-gray-900 text-white p-4 rounded-lg shadow-xl border border-gray-700">
                    <p className="font-bold text-sm mb-2">{data.fullMonthName} {chartYear}</p>
                    <p className="text-red-400 font-semibold">
                        Amount: ${data.amount.toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </p>
                    <p className="text-gray-400 text-xs mt-1">Clawbacks: {data.count}</p>
                </div>
            );
        }
        return null;
    };

    const CustomLabel = ({ x, y, value }: any) => {
        if (value === 0) return null;
        return (
            <text x={x} y={y - 10} fill="#ef4444" textAnchor="middle" fontSize={10} fontWeight="600">
                ${(value/1000).toFixed(1)}k
            </text>
        );
    };

    // ========================================================================
    // ENTERPRISE PATTERN: Filter and sort clawbacks when dependencies change
    // useEffect with state ensures React properly re-renders the table
    // ========================================================================
    useEffect(() => {
        // Create a new array to avoid mutating the original
        let filtered = allClawbacks.slice();

        // Apply broker filter (now also filtered by backend, but keep for consistency)
        if (selectedBrokerId) {
            filtered = filtered.filter((record: any) => record.brokerId === selectedBrokerId);
        }

        // Apply reason filter (from stat cards)
        if (selectedReasonFilter !== 'all') {
            if (selectedReasonFilter === 'needs_categorization') {
                filtered = filtered.filter((record: any) => !record.clawbackReason);
            } else {
                filtered = filtered.filter((record: any) => record.clawbackReason === selectedReasonFilter);
            }
        }

        // Apply sorting based on selected column and direction
        const sorted = [...filtered].sort((a: any, b: any) => {
            let comparison = 0;

            switch (sortBy) {
                case 'percentage':
                    comparison = (Number(a.clawbackPercentage) || 0) - (Number(b.clawbackPercentage) || 0);
                    break;
                case 'customer':
                    comparison = (a.customerName || '').localeCompare(b.customerName || '');
                    break;
                case 'loanId':
                    comparison = (a.loanId || '').localeCompare(b.loanId || '');
                    break;
                case 'broker':
                    comparison = (a.broker || '').localeCompare(b.broker || '');
                    break;
                case 'settlementDate':
                    const settleDateA = a.settlementDate instanceof Date ? a.settlementDate.getTime() : new Date(a.settlementDate || 0).getTime();
                    const settleDateB = b.settlementDate instanceof Date ? b.settlementDate.getTime() : new Date(b.settlementDate || 0).getTime();
                    comparison = settleDateA - settleDateB;
                    break;
                case 'date': // clawback date
                    const dateA = a.invoicedDate instanceof Date ? a.invoicedDate.getTime() : new Date(a.invoicedDate || 0).getTime();
                    const dateB = b.invoicedDate instanceof Date ? b.invoicedDate.getTime() : new Date(b.invoicedDate || 0).getTime();
                    comparison = dateA - dateB;
                    break;
                case 'loanAmount':
                    comparison = (Number(a.loanAmount) || 0) - (Number(b.loanAmount) || 0);
                    break;
                case 'amount': // clawback amount
                    comparison = (Number(a.amountExGst) || 0) - (Number(b.amountExGst) || 0);
                    break;
                case 'lender':
                    comparison = (a.lender || '').localeCompare(b.lender || '');
                    break;
                default:
                    comparison = (Number(a.amountExGst) || 0) - (Number(b.amountExGst) || 0);
            }

            return sortDirection === 'desc' ? -comparison : comparison;
        });

        console.log('[ClawbackPage] Setting displayClawbacks, sortBy:', sortBy, 'direction:', sortDirection, 'count:', sorted.length);
        setDisplayClawbacks(sorted);
    }, [allClawbacks, selectedBrokerId, selectedReasonFilter, sortBy, sortDirection]);

    // Handle column header click for sorting
    const handleSort = useCallback((column: string) => {
        if (sortBy === column) {
            // Toggle direction if same column
            setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
        } else {
            // New column: set it and default to desc for amounts/dates, asc for text
            setSortBy(column);
            const textColumns = ['customer', 'loanId', 'broker', 'lender'];
            setSortDirection(textColumns.includes(column) ? 'asc' : 'desc');
        }
    }, [sortBy]);

    // Sort indicator component
    const SortIcon = ({ column }: { column: string }) => {
        if (sortBy !== column) return <span className="ml-1 text-gray-500">↕</span>;
        return <span className="ml-1 text-blue-400">{sortDirection === 'asc' ? '↑' : '↓'}</span>;
    };

    // Use displayClawbacks for rendering (which triggers re-render on state change)
    const filteredClawbacks = displayClawbacks;

    // Trail-risk follow-up status (server-only client_events, RBAC-scoped),
    // resolved PER OCCURRENCE. A clawback event is keyed (loan, invoice month), so
    // a loan clawed back twice in different months has two rows and two events;
    // the old per-loan lookup could close the wrong month's event.
    //
    // `record.periodMonth` is computed SERVER-side by getClawbackReport with the
    // SAME FORMAT_DATE('%Y-%m', invoiced_date) the materializer groups by. Never
    // derive it here from invoicedDate: the browser timezone can pull a
    // 1st-of-month clawback into the previous month and the ref would match no
    // event.
    const trailRiskRefs = useMemo<TrailRiskRef[]>(
        () =>
            filteredClawbacks
                .map((c: any) =>
                    c?.loanId && c?.periodMonth
                        ? { loanId: c.loanId, eventType: 'clawback' as const, periodMonth: c.periodMonth }
                        : null,
                )
                .filter(Boolean) as TrailRiskRef[],
        [filteredClawbacks],
    );
    const {
        statusByRef: trailRiskByRef,
        error: trailRiskError,
        refetch: refetchTrailRisk,
    } = useTrailRiskStatusForRefs(userData?.companyId, trailRiskRefs);

    /**
     * Returns CSS classes for percentage badge based on severity
     */
    const getPercentageBadge = useCallback((percentage: number): string => {
        if (percentage >= 100) return 'bg-red-900 text-red-300';
        if (percentage >= 75) return 'bg-orange-900 text-orange-300';
        if (percentage >= 50) return 'bg-yellow-900 text-yellow-300';
        return 'bg-gray-700 text-gray-300';
    }, []);

    // ========================================================================
    // JEFF DEAN PRINCIPLE: Memoize calculations to avoid unnecessary work
    // KPI calculations now use backend-provided totals (filtered by broker if needed)
    // ========================================================================
    // NOTE: All amounts are in CENTS - formatCurrency() handles conversion to dollars
    const calculateTotalClawback = useMemo(() => {
        // Backend now filters by brokerId, so totalClawbackAmountCents is already filtered
        // Use backend total directly since it respects all filters (broker, office, template)
        return totalClawbackAmountCents;
    }, [totalClawbackAmountCents]);

    const displayClawbackCount = useMemo(() => {
        // Backend now filters by brokerId, so totalClawbackCount is already filtered
        return totalClawbackCount;
    }, [totalClawbackCount]);

    const total100PercentClawbacks = useMemo(() => {
        return filteredClawbacks.filter((c: any) => c.clawbackPercentage >= 100).length;
    }, [filteredClawbacks]);

    if (loading) {
        return <SkeletonReportPage statCards={4} statColumns={4} tableColumns={9} />;
    }

    if (error) {
        return (
            <div className="space-y-6">
                <PageHeader icon={RotateCcw} title="Clawbacks" />
                <div className="bg-red-900 border border-red-700 text-red-300 p-4 rounded">{error}</div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <PageHeader
                icon={RotateCcw}
                title="Clawbacks"
                subtitle={`Monitor and manage commission clawbacks • ${COMMISSION_GST_BASIS}`}
            />

            {/* ================================================================ */}
            {/* ENTERPRISE PATTERN: KPI Cards with real-time metrics */}
            {/* FIXED: Now shows year-specific data matching the chart */}
            {/* ================================================================ */}
            <div className="bg-gray-800 p-6 rounded-lg shadow-lg mb-6">
                <div className="flex justify-between items-center mb-4">
                    <h3 className="text-lg font-semibold text-white">
                        {chartYear} Overview
                    </h3>
                    <div className="text-xs text-gray-400">
                        Showing data for calendar year {chartYear}
                    </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                    <div>
                        <p className="text-gray-400 text-sm">Total Clawbacks</p>
                        <p className="text-2xl font-bold text-white mt-1">{displayClawbackCount}</p>
                    </div>
                    <div>
                        <p className="text-gray-400 text-sm">Total Clawback Amount</p>
                        <p className="text-2xl font-bold text-red-400 mt-1">{formatCurrency(calculateTotalClawback)}</p>
                    </div>
                    <div>
                        {(() => {
                            // Determine if the selected template is an acquired loan book
                            const selectedTemplateData = selectedTemplate !== 'all'
                                ? fileTemplates.find(t => t.id === selectedTemplate)
                                : null;
                            const isAcquiredBook = selectedTemplateData?.isAcquiredLoanBook || false;

                            // For acquired books: show Clawback/Trail (no upfront in acquired books)
                            // For primary books: show Clawback/Upfront (industry standard)
                            if (isAcquiredBook) {
                                // Acquired book metric: Clawback / Trail
                                if (totalTrailCommissionCents === 0) {
                                    return (
                                        <>
                                            <p className="text-gray-400 text-sm">Clawback / Trail</p>
                                            <div>
                                                <p className="text-2xl font-bold text-gray-500 mt-1">N/A</p>
                                                <p className="text-xs text-gray-500 mt-0.5">No trail data</p>
                                            </div>
                                        </>
                                    );
                                }
                                const rate = (calculateTotalClawback / totalTrailCommissionCents) * 100;
                                // For acquired books, different benchmarks (clawback is expected to be lower % of trail)
                                // Trail is ongoing income, so even 1-2% clawback hurts long-term
                                const colorClass = rate <= 1 ? 'text-green-400'
                                    : rate <= 2 ? 'text-yellow-400'
                                    : rate <= 5 ? 'text-orange-400'
                                    : 'text-red-400';
                                const label = rate <= 1 ? 'Healthy'
                                    : rate <= 2 ? 'Watch'
                                    : rate <= 5 ? 'Concern'
                                    : 'Critical';
                                return (
                                    <>
                                        <p className="text-gray-400 text-sm">Clawback / Trail</p>
                                        <div>
                                            <p className={`text-2xl font-bold ${colorClass} mt-1`}>{formatClawbackRate(rate)}%</p>
                                            <p className={`text-xs ${colorClass} mt-0.5`}>{label}</p>
                                        </div>
                                    </>
                                );
                            }

                            // Primary book metric: Clawback / Upfront (industry standard)
                            if (totalUpfrontCommissionCents === 0) {
                                return (
                                    <>
                                        <p className="text-gray-400 text-sm">Clawback / Upfront</p>
                                        <div>
                                            <p className="text-2xl font-bold text-gray-500 mt-1">N/A</p>
                                            <p className="text-xs text-gray-500 mt-0.5">No upfront data</p>
                                        </div>
                                    </>
                                );
                            }
                            const rate = (calculateTotalClawback / totalUpfrontCommissionCents) * 100;
                            // Industry benchmarks: ≤6% ideal, 6-8% ok, 8-15% bad, >15% critical
                            const colorClass = rate <= 6 ? 'text-green-400'
                                : rate <= 8 ? 'text-yellow-400'
                                : rate <= 15 ? 'text-orange-400'
                                : 'text-red-400';
                            const label = rate <= 6 ? 'Ideal'
                                : rate <= 8 ? 'Acceptable'
                                : rate <= 15 ? 'High'
                                : 'Critical';
                            return (
                                <>
                                    <p className="text-gray-400 text-sm">Clawback / Upfront</p>
                                    <div>
                                        <p className={`text-2xl font-bold ${colorClass} mt-1`}>{formatClawbackRate(rate)}%</p>
                                        <p className={`text-xs ${colorClass} mt-0.5`}>{label}</p>
                                    </div>
                                </>
                            );
                        })()}
                    </div>
                    <div>
                        <p className="text-gray-400 text-sm">100% Clawbacks</p>
                        <p className="text-2xl font-bold text-white mt-1">{total100PercentClawbacks}</p>
                    </div>
                </div>

                {/* Benchmark Legend */}
                <div className="mt-4 pt-4 border-t border-gray-700">
                    {(() => {
                        const selectedTemplateData = selectedTemplate !== 'all'
                            ? fileTemplates.find(t => t.id === selectedTemplate)
                            : null;
                        const isAcquiredBook = selectedTemplateData?.isAcquiredLoanBook || false;

                        if (isAcquiredBook) {
                            return (
                                <div>
                                    <p className="text-xs text-gray-500 mb-2">Clawback / Trail Benchmarks (Acquired Books)</p>
                                    <div className="flex flex-wrap gap-4 text-xs">
                                        <span className="flex items-center gap-1.5">
                                            <span className="w-2 h-2 rounded-full bg-green-400"></span>
                                            <span className="text-gray-400">≤1% Healthy</span>
                                        </span>
                                        <span className="flex items-center gap-1.5">
                                            <span className="w-2 h-2 rounded-full bg-yellow-400"></span>
                                            <span className="text-gray-400">1-2% Watch</span>
                                        </span>
                                        <span className="flex items-center gap-1.5">
                                            <span className="w-2 h-2 rounded-full bg-orange-400"></span>
                                            <span className="text-gray-400">2-5% Concern</span>
                                        </span>
                                        <span className="flex items-center gap-1.5">
                                            <span className="w-2 h-2 rounded-full bg-red-400"></span>
                                            <span className="text-gray-400">&gt;5% Critical</span>
                                        </span>
                                    </div>
                                    <p className="text-xs text-gray-600 mt-2">Trail income is recurring — even small clawback percentages compound over time.</p>
                                </div>
                            );
                        }

                        return (
                            <div>
                                <p className="text-xs text-gray-500 mb-2">Clawback / Upfront Industry Benchmarks</p>
                                <div className="flex flex-wrap gap-4 text-xs">
                                    <span className="flex items-center gap-1.5">
                                        <span className="w-2 h-2 rounded-full bg-green-400"></span>
                                        <span className="text-gray-400">≤6% Ideal</span>
                                    </span>
                                    <span className="flex items-center gap-1.5">
                                        <span className="w-2 h-2 rounded-full bg-yellow-400"></span>
                                        <span className="text-gray-400">6-8% Acceptable</span>
                                    </span>
                                    <span className="flex items-center gap-1.5">
                                        <span className="w-2 h-2 rounded-full bg-orange-400"></span>
                                        <span className="text-gray-400">8-15% High</span>
                                    </span>
                                    <span className="flex items-center gap-1.5">
                                        <span className="w-2 h-2 rounded-full bg-red-400"></span>
                                        <span className="text-gray-400">&gt;15% Critical</span>
                                    </span>
                                </div>
                                <p className="text-xs text-gray-600 mt-2">Industry average is ~8%. Top performers maintain ≤6% through client retention strategies.</p>
                            </div>
                        );
                    })()}
                </div>
            </div>

            {availableYears.length > 0 && (
                <div className="bg-gray-800 p-6 rounded-lg shadow-lg mb-6">
                    <div className="flex justify-between items-center mb-4">
                        <h3 className="font-bold text-lg text-white">Monthly Clawback Trends</h3>
                        <div className="flex items-center gap-2">
                            <label className="text-sm text-gray-400">Year:</label>
                            <select value={chartYear} onChange={(e: any) => setChartYear(parseInt(e.target.value))} className="bg-gray-700 border border-gray-600 text-white text-sm rounded-md px-3 py-1">
                                {availableYears.map((year: any) => (<option key={year} value={year}>{year}</option>))}
                            </select>
                        </div>
                    </div>
                    <ResponsiveContainer width="100%" height={300}>
                        <AreaChart data={monthlyChartData} margin={{ top: 25, right: 30, left: 20, bottom: 5 }}>
                            <defs>
                                <linearGradient id="gradientClawback" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="5%" stopColor="#ef4444" stopOpacity={0.4}/>
                                    <stop offset="95%" stopColor="#ef4444" stopOpacity={0.05}/>
                                </linearGradient>
                            </defs>
                            <XAxis dataKey="month" stroke="#8892b0" fontSize={12} />
                            <YAxis stroke="#8892b0" fontSize={12} tickFormatter={(value: any) => `${(value/1000).toFixed(0)}k`} />
                            <CartesianGrid strokeDasharray="3 3" stroke="#344155" />
                            <Tooltip content={<CustomTooltip />} />
                            <Area
                                type="monotone"
                                dataKey="amount"
                                name="Clawback Amount"
                                stroke="#ef4444"
                                strokeWidth={2}
                                fill="url(#gradientClawback)"
                                dot={{ r: 4, fill: '#ef4444', stroke: '#ef4444' }}
                                activeDot={{ r: 8, fill: '#dc2626' }}
                                label={<CustomLabel />}
                            />
                        </AreaChart>
                    </ResponsiveContainer>
                </div>
            )}
            
            <div className="bg-gray-800 p-4 rounded-lg shadow-md mb-6 flex flex-wrap items-end gap-4">
                <div className="flex-1 min-w-[150px]">
                    <label className="block text-sm font-medium text-gray-400 mb-1">Year</label>
                    <select
                        value={chartYear}
                        onChange={(e: any) => setChartYear(parseInt(e.target.value))}
                        className="w-full bg-gray-700 border border-gray-600 text-white rounded-md p-2"
                    >
                        {availableYears.map((year: any) => (
                            <option key={year} value={year}>{year}</option>
                        ))}
                    </select>
                </div>
                {userData?.role !== 'broker' && userData?.role !== 'broker_assistant' && userVisibleOffices && userVisibleOffices.length > 1 && (
                    <div className="flex-1 min-w-[200px]">
                        <label className="block text-sm font-medium text-gray-400 mb-1">Filter by Office</label>
                        <select
                            value={selectedOffice}
                            onChange={(e: any) => setSelectedOffice(e.target.value)}
                            className="w-full bg-gray-700 border border-gray-600 text-white rounded-md p-2"
                        >
                            <option value="">All Offices</option>
                            {userVisibleOffices.map((office) => (
                                <option key={office.id} value={office.id}>{office.name}</option>
                            ))}
                        </select>
                    </div>
                )}
                {(userData?.role === 'administrator' || userData?.role === 'super_admin' || userData?.role === 'office_admin') && (
                    <div className="flex-1 min-w-[200px]">
                        <label className="block text-sm font-medium text-gray-400 mb-1">Filter by Broker</label>
                        <select
                            value={selectedBrokerId || ''}
                            onChange={(e: any) => {
                                const brokerId = e.target.value;
                                if (!brokerId) {
                                    // Reset to show all brokers
                                    setSelectedBrokerId(undefined);
                                    setSelectedBroker('');
                                } else {
                                    // Find the broker display name from allBrokersForDropdown (full list)
                                    const broker = allBrokersForDropdown.find((b) => b.brokerId === brokerId);
                                    setSelectedBrokerId(brokerId);
                                    setSelectedBroker(broker?.brokerDisplay || brokerId);
                                }
                            }}
                            className="w-full bg-gray-700 border border-gray-600 text-white rounded-md p-2"
                        >
                            <option value="">All Brokers</option>
                            {[...allBrokersForDropdown]
                                .sort((a, b) => (a.brokerDisplay || '').localeCompare(b.brokerDisplay || ''))
                                .map((broker) => (
                                    <option key={broker.brokerId} value={broker.brokerId}>{broker.brokerDisplay}</option>
                                ))}
                        </select>
                    </div>
                )}
                {loanBooks.length > 0 && (
                    <div className="flex-1 min-w-[200px]">
                        <label className="block text-sm font-medium text-gray-400 mb-1">Loan Book</label>
                        <select
                            value={selectedTemplate}
                            onChange={(e: any) => setSelectedTemplate(e.target.value)}
                            className="w-full bg-gray-700 border border-gray-600 text-white rounded-md p-2"
                        >
                            <option value="all">All Loan Books</option>
                            {getDisplayLoanBooks(loanBooks).map((opt) => (
                                <option key={opt.value} value={opt.value}>{opt.label}</option>
                            ))}
                        </select>
                    </div>
                )}
                <div className="flex-1 min-w-[200px]">
                    <label className="block text-sm font-medium text-gray-400 mb-1">Quick Sort</label>
                    <select
                        value={`${sortBy}-${sortDirection}`}
                        onChange={(e: any) => {
                            const [col, dir] = e.target.value.split('-');
                            setSortBy(col);
                            setSortDirection(dir as 'asc' | 'desc');
                        }}
                        className="w-full bg-gray-700 border border-gray-600 text-white rounded-md p-2"
                    >
                        <option value="amount-desc">Amount (Highest First)</option>
                        <option value="amount-asc">Amount (Lowest First)</option>
                        <option value="percentage-desc">Clawback % (Highest First)</option>
                        <option value="percentage-asc">Clawback % (Lowest First)</option>
                        <option value="date-desc">Clawback Date (Newest First)</option>
                        <option value="date-asc">Clawback Date (Oldest First)</option>
                        <option value="broker-asc">Broker (A-Z)</option>
                        <option value="lender-asc">Lender (A-Z)</option>
                    </select>
                </div>
            </div>

            {/* ================================================================ */}
            {/* Clawback Reason Stat Cards - Click to filter by reason */}
            {/* ================================================================ */}
            <div className="mb-6">
                <ClawbackReasonStatCards
                    metrics={metricsByReason}
                    selectedReason={selectedReasonFilter}
                    onReasonSelect={setSelectedReasonFilter}
                    isLoading={loading}
                />
            </div>

            {filteredClawbacks.length === 0 ? (
                <div className="bg-gray-800 p-12 rounded-lg shadow-lg text-center">
                    <h3 className="text-xl font-semibold mb-2">No Clawbacks Found</h3>
                    <p className="text-gray-400">
                        {selectedReasonFilter !== 'all'
                            ? selectedReasonFilter === 'needs_categorization'
                                ? `All clawbacks in ${chartYear} have been categorized`
                                : `No clawbacks with reason "${CLAWBACK_REASON_LABELS[selectedReasonFilter]}" in ${chartYear}`
                            : selectedBroker && selectedTemplate !== 'all'
                                ? `No clawbacks found for ${selectedBroker} in ${fileTemplates.find(t => t.id === selectedTemplate)?.name || 'selected loan book'} for ${chartYear}`
                                : selectedBroker
                                    ? `No clawbacks found for ${selectedBroker} in ${chartYear}`
                                    : selectedTemplate !== 'all'
                                        ? `No clawbacks found for ${fileTemplates.find(t => t.id === selectedTemplate)?.name || 'selected loan book'} in ${chartYear}`
                                        : `No clawbacks have been recorded in ${chartYear}`}
                    </p>
                    {selectedReasonFilter !== 'all' && (
                        <button
                            onClick={() => setSelectedReasonFilter('all')}
                            className="mt-4 text-blue-400 hover:text-blue-300 text-sm"
                        >
                            Clear reason filter
                        </button>
                    )}
                </div>
            ) : (
                <div className="bg-gray-800 rounded-lg shadow-lg overflow-hidden">
                    <div className="overflow-x-auto">
                        {/* Responsive table with rem-based sizing */}
                        <table className="w-full text-[0.8125rem] text-left text-gray-400">
                            <thead className="text-[0.6875rem] text-gray-400 uppercase bg-gray-700">
                                <tr>
                                    <th scope="col" className="px-3 py-2 cursor-pointer hover:bg-gray-600 select-none whitespace-nowrap" onClick={() => handleSort('percentage')}>
                                        %<SortIcon column="percentage" />
                                    </th>
                                    <th scope="col" className="px-3 py-2 cursor-pointer hover:bg-gray-600 select-none" onClick={() => handleSort('customer')}>
                                        Customer<SortIcon column="customer" />
                                    </th>
                                    <th scope="col" className="px-3 py-2 cursor-pointer hover:bg-gray-600 select-none whitespace-nowrap" onClick={() => handleSort('loanAmount')}>
                                        Loan<SortIcon column="loanAmount" />
                                    </th>
                                    <th scope="col" className="px-3 py-2 cursor-pointer hover:bg-gray-600 select-none whitespace-nowrap" onClick={() => handleSort('amount')}>
                                        Clawback<SortIcon column="amount" />
                                    </th>
                                    <th scope="col" className="px-3 py-2 cursor-pointer hover:bg-gray-600 select-none whitespace-nowrap text-center" onClick={() => handleSort('settlementDate')}>
                                        Settled<SortIcon column="settlementDate" />
                                    </th>
                                    <th scope="col" className="px-3 py-2 cursor-pointer hover:bg-gray-600 select-none whitespace-nowrap text-center" onClick={() => handleSort('date')}>
                                        Clawback<SortIcon column="date" />
                                    </th>
                                    <th scope="col" className="px-3 py-2 cursor-pointer hover:bg-gray-600 select-none" onClick={() => handleSort('broker')}>
                                        Broker<SortIcon column="broker" />
                                    </th>
                                    <th scope="col" className="px-3 py-2 cursor-pointer hover:bg-gray-600 select-none" onClick={() => handleSort('lender')}>
                                        Lender<SortIcon column="lender" />
                                    </th>
                                    <th scope="col" className="px-3 py-2 whitespace-nowrap">
                                        Reason
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredClawbacks.map((record: any, index: number) => {
                                    // Use the actual clawback amount (amountExGst is already the commission amount)
                                    const clawbackAmount = typeof record.amountExGst === 'number' ? record.amountExGst : parseFloat(record.amountExGst || '0');
                                    return (
                                        // Use index in key to handle duplicate record.id (same loan with multiple clawbacks)
                                        <tr key={`${record.id}-${index}`} className="bg-gray-800 border-b border-gray-700 hover:bg-gray-750">
                                            <td className="px-3 py-2">
                                                <span className={`inline-block px-1.5 py-0.5 text-[0.6875rem] font-semibold rounded-full ${getPercentageBadge(record.clawbackPercentage)}`}>
                                                    {record.clawbackPercentage}%
                                                </span>
                                            </td>
                                            <td className="px-3 py-2 font-medium text-white">
                                                <div className="max-w-[10rem] truncate" title={record.customerName || 'N/A'}>
                                                    {record.customerName || 'N/A'}
                                                </div>
                                            </td>
                                            <td className="px-3 py-2 text-gray-300 whitespace-nowrap">{formatCurrency(record.loanAmount)}</td>
                                            <td className="px-3 py-2 text-red-400 font-semibold whitespace-nowrap">{formatCurrency(clawbackAmount)}</td>
                                            <td className="px-3 py-2"><StackedDate dateValue={record.settlementDate} /></td>
                                            <td className="px-3 py-2"><StackedDate dateValue={record.invoicedDate} /></td>
                                            <td className="px-3 py-2 font-medium text-white">
                                                <div className="max-w-[8rem] truncate" title={record.broker || 'N/A'}>
                                                    {record.broker || 'N/A'}
                                                </div>
                                            </td>
                                            <td className="px-3 py-2">
                                                <div className="max-w-[6rem] truncate" title={record.lender || 'N/A'}>
                                                    {record.lender || 'N/A'}
                                                </div>
                                            </td>
                                            <td className="px-3 py-2">
                                                <select
                                                    value={record.clawbackReason || ''}
                                                    onChange={async (e) => {
                                                        const newReason = e.target.value as ClawbackReason;
                                                        if (newReason) {
                                                            await setClawbackReason({
                                                                clawbackIds: [record.id],
                                                                loanIds: [record.loanId],
                                                                reason: newReason,
                                                            });
                                                        }
                                                    }}
                                                    disabled={isBrokerFilteredAdmin || isSaving}
                                                    title={isBrokerFilteredAdmin ? BROKER_FILTERED_ADMIN_COPY : undefined}
                                                    className={`text-[0.6875rem] rounded px-1.5 py-1 border cursor-pointer focus:ring-1 focus:ring-blue-500 min-w-[7rem] ${
                                                        record.clawbackReason
                                                            ? `${CLAWBACK_REASON_COLORS[record.clawbackReason as ClawbackReason]?.bg || 'bg-gray-700'} ${CLAWBACK_REASON_COLORS[record.clawbackReason as ClawbackReason]?.text || 'text-gray-400'} ${CLAWBACK_REASON_COLORS[record.clawbackReason as ClawbackReason]?.border || 'border-gray-600'}`
                                                            : 'bg-amber-900/30 text-amber-400 border-amber-500/30'
                                                    }`}
                                                >
                                                    <option value="" className="bg-gray-800 text-gray-400">Uncategorised</option>
                                                    {ALL_CLAWBACK_REASONS.map((reason) => (
                                                        <option key={reason} value={reason} className="bg-gray-800 text-white">
                                                            {CLAWBACK_REASON_LABELS[reason]}
                                                        </option>
                                                    ))}
                                                </select>
                                                {userData?.companyId && (() => {
                                                    if (!record.periodMonth) return null;
                                                    const status = trailRiskByRef.get(
                                                        refKey({
                                                            loanId: record.loanId,
                                                            eventType: 'clawback',
                                                            periodMonth: record.periodMonth,
                                                        }),
                                                    );
                                                    // Nothing to say and no failure to report: stay silent.
                                                    if (!status && !trailRiskError) return null;
                                                    return (
                                                        <div className="mt-1.5">
                                                            <TrailRiskFollowUp
                                                                companyId={userData.companyId}
                                                                status={status}
                                                                statusUnavailable={Boolean(trailRiskError)}
                                                                onChanged={refetchTrailRisk}
                                                            />
                                                        </div>
                                                    );
                                                })()}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ClawbackPage;
